<template>
  <b-card-code title="Gépek kezelése">
    <b-alert
      show
      variant="info"
      class="mb-2"
    >
      Gépek felvétele és módosítása. Törlés csak admin jogosultsággal.
    </b-alert>

    <b-form @submit.prevent="submitForm">
      <b-row>
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group label="Gép neve *">
            <b-form-input
              v-model="form.name"
              :disabled="isSaving || !canManage"
              placeholder="pl. CAT 320"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <div class="d-flex flex-wrap">
        <b-button
          v-if="canManage"
          variant="primary"
          type="submit"
          size="sm"
          :disabled="isSaving"
          class="mr-50 mb-50"
        >
          {{ isEdit ? (isSaving ? 'Mentés...' : 'Módosítás mentése') : (isSaving ? 'Mentés...' : 'Új gép mentése') }}
        </b-button>

        <b-button
          v-if="canManage && isEdit"
          variant="outline-secondary"
          size="sm"
          class="mb-50"
          @click="resetForm"
        >
          Mégse
        </b-button>
      </div>
    </b-form>

    <b-alert
      v-if="!canManage"
      show
      variant="warning"
      class="mt-2 mb-2"
    >
      Nincs jogosultságod a gépek kezeléséhez.
    </b-alert>

    <b-table
      :items="machines"
      :fields="tableFields"
      small
      responsive
      striped
      class="mb-0 mt-2"
      empty-text="Nincs rögzített gép."
    >
      <template #cell(project)="{ item }">
        <b-form-select
          v-if="isTankolasAdmin"
          :value="normalizeProjectId(item.current_project_id)"
          :options="projectOptions"
          :disabled="isProjectSaving(item.id)"
          @input="value => saveMachineProject(item, value)"
        />
      </template>

      <template #cell(actions)="{ item }">
        <div class="d-flex align-items-center flex-wrap">
          <b-button
            v-if="canManage"
            variant="outline-primary"
            size="sm"
            class="mr-50 mb-50"
            @click="startEdit(item)"
          >
            Szerkesztés
          </b-button>

          <b-button
            v-if="isTankolasAdmin"
            variant="outline-danger"
            size="sm"
            class="mb-50"
            @click="deleteItem(item)"
          >
            Törlés
          </b-button>
        </div>
      </template>
    </b-table>
  </b-card-code>
</template>

<script>
import BCardCode from '@core/components/b-card-code/BCardCode.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { checkRole } from '@/middleware/authLoader'
import axios from 'axios'
import {
  BAlert,
  BButton,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BRow,
  BTable,
} from 'bootstrap-vue'

export default {
  name: 'TankolasMachines',
  components: {
    BCardCode,
    BAlert,
    BButton,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BRow,
    BTable,
  },
  data() {
    return {
      isSaving: false,
      editId: null,
      machines: [],
      projects: [],
      savingProjectIds: [],
      form: {
        name: '',
      },
    }
  },
  computed: {
    isTankolasAdmin() {
      return checkRole('tankolas_role_admin')
    },
    canManage() {
      return checkRole('tankolas_role_halado') || checkRole('tankolas_role_admin')
    },
    isEdit() {
      return !!this.editId
    },
    tableFields() {
      const fields = [{ key: 'name', label: 'Gép neve' }]
      if (this.isTankolasAdmin) {
        fields.push({ key: 'project', label: 'Projekt' })
      }
      fields.push({ key: 'actions', label: 'Művelet' })
      return fields
    },
    projectOptions() {
      return [
        { value: null, text: 'Nincs hozzárendelve' },
        ...this.projects.map(project => ({
          value: project.id,
          text: this.projectLabel(project),
        })),
      ]
    },
  },
  mounted() {
    this.loadItems()
    if (this.isTankolasAdmin) {
      this.loadProjects()
    }
  },
  methods: {
    authHeaders() {
      return {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      }
    },
    toastError(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message || 'Mentés sikertelen.',
          icon: 'XOctagonIcon',
          variant: 'danger',
        },
      })
    },
    toastSuccess(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message || 'Mentve.',
          icon: 'CheckIcon',
          variant: 'success',
        },
      })
    },
    extractBackendError(error) {
      if (!error || !error.response || !error.response.data) return 'Mentés sikertelen.'
      const { data } = error.response
      if (data.message) return data.message
      if (data.errors && typeof data.errors === 'object') {
        const firstField = Object.keys(data.errors)[0]
        if (firstField && Array.isArray(data.errors[firstField]) && data.errors[firstField][0]) {
          return data.errors[firstField][0]
        }
      }
      return 'Mentés sikertelen.'
    },
    validateForm() {
      if (!this.form.name || !this.form.name.trim()) return 'A gép neve kötelező.'
      return null
    },
    async loadItems() {
      try {
        const response = await axios({
          method: 'get',
          url: 'tankolas/machines',
          headers: this.authHeaders(),
        })

        this.machines = Array.isArray(response.data.items) ? response.data.items : []
      } catch (error) {
        this.toastError('A gép lista nem tölthető be.')
      }
    },
    async loadProjects() {
      try {
        const response = await axios({
          method: 'get',
          url: 'tankolas/projects',
          headers: this.authHeaders(),
        })

        this.projects = Array.isArray(response.data.items) ? response.data.items : []
      } catch (error) {
        this.toastError('A projekt lista nem tölthető be.')
      }
    },
    projectLabel(project) {
      const workNumber = (project.work_number || '').toString().trim()
      const name = (project.name || '').toString().trim()
      if (workNumber && name) {
        return `${workNumber} | ${name}`
      }
      return name || workNumber || `Projekt #${project.id}`
    },
    normalizeProjectId(value) {
      if (value === null || value === undefined || value === '') return null
      const parsed = Number(value)
      return Number.isNaN(parsed) ? null : parsed
    },
    isProjectSaving(machineId) {
      return this.savingProjectIds.includes(machineId)
    },
    async saveMachineProject(item, value) {
      if (!this.isTankolasAdmin) return

      const machineId = Number(item.id)
      const currentProjectId = this.normalizeProjectId(value)

      if (!Number.isInteger(machineId) || machineId <= 0) return

      this.savingProjectIds = [...this.savingProjectIds, machineId]

      try {
        const response = await axios({
          method: 'put',
          url: `tankolas/machines/${machineId}`,
          headers: this.authHeaders(),
          data: {
            current_project_id: currentProjectId,
          },
        })

        const updated = response?.data?.item || null
        this.machines = this.machines.map(machine => {
          if (Number(machine.id) !== machineId) return machine
          return {
            ...machine,
            current_project_id: updated ? updated.current_project_id : currentProjectId,
          }
        })

        this.toastSuccess('Projekt hozzárendelés mentve.')
      } catch (error) {
        this.toastError(this.extractBackendError(error))
        await this.loadItems()
      } finally {
        this.savingProjectIds = this.savingProjectIds.filter(id => id !== machineId)
      }
    },
    resetForm() {
      this.editId = null
      this.form.name = ''
    },
    startEdit(item) {
      this.editId = item.id
      this.form.name = item.name || ''
    },
    async submitForm() {
      if (!this.canManage) {
        this.toastError('Nincs jogosultságod a gépek kezeléséhez.')
        return
      }

      const validationError = this.validateForm()
      if (validationError) {
        this.toastError(validationError)
        return
      }

      this.isSaving = true
      try {
        const { isEdit } = this
        const response = await axios({
          method: isEdit ? 'put' : 'post',
          url: isEdit ? `tankolas/machines/${this.editId}` : 'tankolas/machines',
          headers: this.authHeaders(),
          data: {
            name: this.form.name,
          },
        })

        this.toastSuccess(response.data.message || 'Mentve.')
        this.resetForm()
        await this.loadItems()
      } catch (error) {
        this.toastError(this.extractBackendError(error))
      } finally {
        this.isSaving = false
      }
    },
    async deleteItem(item) {
      if (!this.isTankolasAdmin) return
      if (!window.confirm(`Biztosan törlöd ezt a gépet: ${item.name}?`)) return

      try {
        const response = await axios({
          method: 'delete',
          url: `tankolas/machines/${item.id}`,
          headers: this.authHeaders(),
        })

        this.toastSuccess(response.data.message || 'Törölve.')
        await this.loadItems()
      } catch (error) {
        this.toastError(this.extractBackendError(error))
      }
    },
  },
}
</script>
